import React, { useEffect, useState } from "react";
import { Plus, X, Trash2, Building2, User } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function CustomerModal({ open, onClose, onSaved, editing }) {
    const [form, setForm] = useState({ name: "", type: "azienda", email: "", phone: "", vat: "", notes: "" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (editing) setForm({ name: editing.name || "", type: editing.type || "azienda", email: editing.email || "", phone: editing.phone || "", vat: editing.vat || "", notes: editing.notes || "" });
        else setForm({ name: "", type: "azienda", email: "", phone: "", vat: "", notes: "" });
        setError("");
    }, [editing, open]);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const payload = { ...form };
            ["email", "phone", "vat", "notes"].forEach(k => { if (!payload[k]) delete payload[k]; });
            if (editing) await api.put(`/customers/${editing.id}`, payload);
            else await api.post("/customers", payload);
            onSaved();
            onClose();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(13,20,33,.5)" }} onClick={onClose} data-testid="customer-modal">
            <div className="card-tpl w-full max-w-lg p-7" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-5">
                    <h3 className="text-[20px] font-extrabold tracking-tight" style={{ color: "var(--navy)" }}>
                        {editing ? "Modifica cliente" : "Nuovo cliente"}
                    </h3>
                    <button onClick={onClose} className="w-8 h-8 rounded-md grid place-items-center hover:bg-stone-100" data-testid="cust-modal-close">
                        <X size={16} />
                    </button>
                </div>

                <form onSubmit={submit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <button type="button" onClick={() => setForm(p => ({ ...p, type: "azienda" }))}
                            className="p-3 rounded-lg flex items-center gap-2 transition-all"
                            style={{ background: form.type === "azienda" ? "var(--mod-accent-soft)" : "var(--surface)", border: `2px solid ${form.type === "azienda" ? "var(--mod-accent)" : "var(--line-2)"}` }}>
                            <Building2 size={18} style={{ color: form.type === "azienda" ? "var(--mod-accent)" : "var(--muted)" }} />
                            <span className="font-bold text-[13px]" style={{ color: form.type === "azienda" ? "var(--mod-accent)" : "var(--text)" }}>Azienda</span>
                        </button>
                        <button type="button" onClick={() => setForm(p => ({ ...p, type: "privato" }))}
                            className="p-3 rounded-lg flex items-center gap-2 transition-all"
                            style={{ background: form.type === "privato" ? "var(--mod-accent-soft)" : "var(--surface)", border: `2px solid ${form.type === "privato" ? "var(--mod-accent)" : "var(--line-2)"}` }}>
                            <User size={18} style={{ color: form.type === "privato" ? "var(--mod-accent)" : "var(--muted)" }} />
                            <span className="font-bold text-[13px]" style={{ color: form.type === "privato" ? "var(--mod-accent)" : "var(--text)" }}>Privato</span>
                        </button>
                    </div>

                    <div>
                        <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-1" style={{ color: "var(--muted)" }}>Nome/Ragione sociale *</label>
                        <input data-testid="cust-name" required value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className="input" />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-1" style={{ color: "var(--muted)" }}>Email</label>
                            <input data-testid="cust-email" type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} className="input" />
                        </div>
                        <div>
                            <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-1" style={{ color: "var(--muted)" }}>Telefono</label>
                            <input data-testid="cust-phone" value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} className="input" />
                        </div>
                    </div>

                    {form.type === "azienda" && (
                        <div>
                            <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-1" style={{ color: "var(--muted)" }}>P.IVA / C.F.</label>
                            <input data-testid="cust-vat" value={form.vat} onChange={(e) => setForm(p => ({ ...p, vat: e.target.value }))} className="input" />
                        </div>
                    )}

                    <div>
                        <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-1" style={{ color: "var(--muted)" }}>Note</label>
                        <textarea data-testid="cust-notes" rows={2} value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} className="input" />
                    </div>

                    {error && <div className="p-3 rounded-lg text-[13px]" style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", color: "#B91C1C" }}>{error}</div>}

                    <div className="flex gap-2 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg font-semibold text-[13px]" style={{ background: "var(--surface)", border: "1px solid var(--line-2)", color: "var(--text)" }}>Annulla</button>
                        <button data-testid="cust-submit" type="submit" disabled={loading} className="btn-grad flex-1 py-2.5 rounded-lg font-bold text-[13px]">
                            {loading ? "Salvataggio..." : (editing ? "Salva" : "Crea cliente")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function Clienti() {
    const [customers, setCustomers] = useState([]);
    const [search, setSearch] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        const { data } = await api.get("/customers");
        setCustomers(data);
    };

    useEffect(() => { load(); }, []);

    const del = async (id) => {
        if (!window.confirm("Eliminare il cliente?")) return;
        try {
            await api.delete(`/customers/${id}`);
            load();
        } catch (e) {
            alert(formatApiError(e));
        }
    };

    const filtered = customers.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="space-y-6" data-testid="clienti-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1
                        className="tracking-tight"
                        style={{
                            color: "var(--navy)",
                            fontFamily: "Geist, Outfit, sans-serif",
                            fontSize: "clamp(1.7rem, 2.8vw, 2.2rem)",
                            fontWeight: 200,
                            letterSpacing: "-0.035em",
                            lineHeight: 1.05,
                        }}
                    >
                        <span style={{ fontWeight: 600 }}>Clienti</span>
                    </h1>
                    <p className="text-[13px] mt-1" style={{ color: "var(--text-2)" }}>{customers.length} clienti registrati</p>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        data-testid="cust-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Cerca cliente..."
                        className="input"
                        style={{ width: 240 }}
                    />
                    <button data-testid="cust-new" onClick={() => { setEditing(null); setModalOpen(true); }} className="btn-grad px-4 py-2.5 rounded-lg text-[13px] font-bold flex items-center gap-2">
                        <Plus size={14} /> Nuovo cliente
                    </button>
                </div>
            </div>

            {filtered.length === 0 ? (
                <div className="card-tpl p-12 text-center">
                    <Building2 size={28} className="mx-auto mb-3" style={{ color: "var(--muted-2)" }} />
                    <div className="font-bold" style={{ color: "var(--navy)" }}>Nessun cliente</div>
                    <p className="text-[13px] mt-2 mb-4" style={{ color: "var(--text-2)" }}>Aggiungi il primo cliente per iniziare.</p>
                    <button data-testid="empty-cust-new" onClick={() => { setEditing(null); setModalOpen(true); }} className="btn-grad inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-bold">
                        <Plus size={14} /> Aggiungi cliente
                    </button>
                </div>
            ) : (
                <div className="card-tpl overflow-hidden">
                    <table className="w-full text-left">
                        <thead style={{ background: "var(--bg)", borderBottom: "1px solid var(--line)" }}>
                            <tr>
                                <th className="mono text-[10px] uppercase tracking-[0.12em] font-bold px-4 py-3" style={{ color: "var(--muted)" }}>Cliente</th>
                                <th className="mono text-[10px] uppercase tracking-[0.12em] font-bold px-4 py-3" style={{ color: "var(--muted)" }}>Tipo</th>
                                <th className="mono text-[10px] uppercase tracking-[0.12em] font-bold px-4 py-3" style={{ color: "var(--muted)" }}>Contatti</th>
                                <th className="mono text-[10px] uppercase tracking-[0.12em] font-bold px-4 py-3" style={{ color: "var(--muted)" }}>P.IVA/CF</th>
                                <th className="px-4 py-3 w-20"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((c) => (
                                <tr key={c.id} data-testid={`cust-row-${c.id}`} className="hover:bg-stone-50 cursor-pointer transition" style={{ borderBottom: "1px solid var(--line)" }} onClick={() => { setEditing(c); setModalOpen(true); }}>
                                    <td className="px-4 py-3.5">
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-8 h-8 rounded-md grid place-items-center text-white text-[11px] font-bold" style={{ background: c.type === "azienda" ? "var(--navy)" : "var(--cobalt)" }}>
                                                {c.name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
                                            </div>
                                            <span className="font-bold text-[14px]" style={{ color: "var(--navy)" }}>{c.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3.5">
                                        <span className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold" style={{ background: c.type === "azienda" ? "var(--cobalt-soft)" : "var(--mod-accent-soft)", color: c.type === "azienda" ? "var(--cobalt)" : "var(--mod-accent)" }}>
                                            {c.type}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3.5 text-[12px]" style={{ color: "var(--text-2)" }}>
                                        {c.email && <div>{c.email}</div>}
                                        {c.phone && <div className="mono">{c.phone}</div>}
                                    </td>
                                    <td className="px-4 py-3.5 mono text-[12px]" style={{ color: "var(--muted)" }}>{c.vat || "—"}</td>
                                    <td className="px-4 py-3.5 text-right">
                                        <button onClick={(e) => { e.stopPropagation(); del(c.id); }} className="text-[11px] hover:underline" style={{ color: "var(--red)" }}>
                                            <Trash2 size={13} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <CustomerModal
                open={modalOpen}
                onClose={() => { setModalOpen(false); setEditing(null); }}
                onSaved={load}
                editing={editing}
            />
        </div>
    );
}
